import { useEffect, useMemo, useState } from "preact/hooks";
import type { AtlanticRow, CoreData, InsightsData, MoveRow, SexIdx } from "../data/types";
import { useStore } from "../state/store";
import { settings } from "../state/app";
import { openName } from "../state/router";
import { crossEnds, familyOf } from "../data/analysis";
import { entryFor, loadInsights } from "../data/load";
import { nameKey } from "../data/keys";
import { SEX_WORD, fmt, share } from "../data/format";
import { HistoryChart } from "./charts";
import { Segmented } from "./FilterSheet";
import { IconArrow } from "./icons";

const pct = (tenths: number) => `${(tenths / 10).toFixed(tenths >= 100 ? 0 : 1).replace(/\.0$/, "")}%`;

/** "-a", or "an ee sound" */
function endingLabel(e: string): string {
  return e === "ee" ? "An ‘ee’ sound" : `-${e}`;
}

/** One series in a small, unlabelled frame: the text beside it carries the numbers. */
function MiniLine({ values, max, cls, mid }: { values: (number | null)[]; max: number; cls: string; mid?: number }) {
  const W = 120;
  const H = 40;
  const n = values.length;
  if (n < 2) return null;
  const x = (i: number) => (i / (n - 1)) * W;
  const y = (v: number) => H - 2 - (v / max) * (H - 4);
  let d = "";
  let pen = false;
  values.forEach((v, i) => {
    if (v === null) {
      pen = false;
      return;
    }
    d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    pen = true;
  });
  const first = values.findIndex((v) => v !== null);
  let last = n - 1;
  while (last > 0 && values[last] === null) last--;
  const area = first >= 0 && values.every((v) => v !== null) ? `${d}L${x(last)},${H}L${x(first)},${H}Z` : "";
  return (
    <svg class={`mini-line ${cls}`} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      {mid !== undefined && <line x1={0} x2={W} y1={y(mid)} y2={y(mid)} class="ml-mid" />}
      {area && <path d={area} class="ml-area" />}
      <path d={d} class="ml-line" vector-effect="non-scaling-stroke" />
    </svg>
  );
}

function Card({ id, title, lede, children }: { id: string; title: string; lede?: string; children: preact.ComponentChildren }) {
  return (
    <section class="trend" aria-labelledby={`trend-${id}`}>
      <h2 id={`trend-${id}`}>{title}</h2>
      {lede && <p class="trend-lede">{lede}</p>}
      {children}
    </section>
  );
}

function NameButton({ core, k, name }: { core: CoreData; k: string; name?: string }) {
  return (
    <button class="link-btn inline" onClick={() => openName(k)}>
      {name ?? entryFor(core, k).name}
    </button>
  );
}

/** names in a sentence, each one opening its page: "Eliana, Anaya and Alba" */
function NameList({ core, keys }: { core: CoreData; keys: string[] }) {
  return (
    <>
      {keys.map((k, i) => (
        <span key={k}>
          {i > 0 && (i === keys.length - 1 ? " and " : ", ")}
          <NameButton core={core} k={k} />
        </span>
      ))}
    </>
  );
}

function MoveList({ core, rows, dir, label }: { core: CoreData; rows: MoveRow[]; dir: "up" | "down"; label: string }) {
  return (
    <div class="move-col">
      <h3>{label}</h3>
      <ol class="rows trend-rows">
        {rows.map(([k, now, prev]) => (
          <li key={k}>
            <button class="trend-row" onClick={() => openName(k)}>
              <span class="tr-name">{entryFor(core, k).name}</span>
              <span class="tr-count">
                #{fmt(now)}
                <span class="sr-only">, from #{fmt(prev)}</span>
              </span>
              <span class={`tr-move ${dir}`}>
                <IconArrow dir={dir} size={12} />
                {Math.abs(prev - now)}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

function AtlanticList({ core, rows, here, there, label }: { core: CoreData; rows: AtlanticRow[]; here: string; there: string; label: string }) {
  return (
    <div class="move-col">
      <h3>{label}</h3>
      <ol class="rows trend-rows">
        {rows.slice(0, 6).map(([name, rh, rt]) => {
          const k = nameKey(name);
          const open = core.byKey.has(k);
          const body = (
            <>
              <span class="tr-name">{name}</span>
              <span class="tr-sub">
                #{fmt(rh)} in {here} · {rt ? `#${fmt(rt)}` : "not listed"} in {there}
              </span>
            </>
          );
          return (
            <li key={name}>
              {open ? (
                <button class="trend-row two" onClick={() => openName(k)}>
                  {body}
                </button>
              ) : (
                <div class="trend-row two">{body}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Explore → Trends: the stories in the records, girls or boys. */
export function TrendsSection({ core, onEnding }: { core: CoreData; onEnding: (ending: string, si: SexIdx) => void }) {
  const pref = useStore(settings, (s) => s.sex);
  const [si, setSi] = useState<SexIdx>(pref === "M" ? 1 : 0);
  const [ins, setIns] = useState<InsightsData | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setIns(null);
    loadInsights()
      .then((d) => live && setIns(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [core]);

  const sx = si ? "M" : "F";
  const word = SEX_WORD[si];
  const c = core.country;
  const years = useMemo(() => (ins ? Array.from({ length: ins.variety.F.half.length }, (_, i) => ins.y0 + i) : []), [ins]);

  if (!ins)
    return (
      <div class="xpage">
        <h1 class="xsec-title">Trends</h1>
        <p class="muted pad">
          {failed ? (__NATIVE__ ? "Couldn't load the trends. Please try again." : "Couldn't load the trends. Check your connection and try again.") : "Loading…"}
        </p>
      </div>
    );

  // ---- every spelling counted: a new #1, or else the row that moves furthest
  const real = ins.real[sx].slice(0, 10);
  const jump =
    real[0] && real[0][2] !== 1
      ? { i: 0, by: real[0][2] - 1 }
      : real.reduce<{ i: number; by: number } | null>((best, [, , own], i) => {
          const by = own ? own - (i + 1) : 0;
          return by > (best?.by ?? 2) ? { i, by } : best;
        }, null);
  const possessive = si ? "boys'" : "girls'";
  // no new order at the top: say how far ahead the leader is with its spellings, if that's the story
  const ahead =
    !jump && real.length > 1 && (familyOf(ins, real[0][0], si)?.members.length ?? 1) > 1 && real[0][1] >= 1.8 * real[1][1]
      ? real[0][1] / real[1][1]
      : 0;

  // ---- movers
  const mv = ins.moves?.[sx];
  const up = mv?.up[0];
  const down = mv?.down[0];

  // ---- variety
  const v = ins.variety[sx];
  const half0 = v.half[0];
  const halfN = v.half[v.half.length - 1];

  // ---- endings: the four that changed most, on one scale
  const ends = ins.endings[sx].slice(0, 4);
  const endMax = Math.max(1, ...ends.flatMap((e) => e.s));
  const riser = ends.slice().sort((a, b) => b.s[b.s.length - 1] - b.s[0] - (a.s[a.s.length - 1] - a.s[0]))[0];

  // ---- crossing over
  const cross = ins.cross.slice(0, 6).map((x) => ({ k: x.k, s: x.s.map((v) => (v < 0 ? null : v / 1000)), ...crossEnds(x) }));

  // ---- across the Atlantic, from here
  const a = ins.atlantic;
  const usFirst = c.id === "us";
  const lists: { rows: AtlanticRow[]; here: string; there: string; label: string }[] = [
    { rows: a.uk[sx], here: "the UK", there: "the US", label: c.id === "uk" ? "Big here, rare in the US" : "Big in the UK, rare in the US" },
    { rows: a.us[sx], here: "the US", there: "the UK", label: c.id === "us" ? "Big here, rare in the UK" : "Big in the US, rare in the UK" },
  ];
  if (usFirst) lists.reverse();
  const lead = lists[0].rows[0];

  return (
    <div class="xpage trends">
      <h1 class="xsec-title">Trends</h1>
      <p class="xsec-sub">
        What {c.id === "other" ? "the UK and US records show" : `${c.name}'s records show`}, {ins.y0} to {ins.latest}.
      </p>
      <Segmented<"0" | "1">
        label="Girls or boys"
        value={String(si) as "0" | "1"}
        onChange={(val) => setSi(Number(val) as SexIdx)}
        options={[
          { v: "0", label: "Girls" },
          { v: "1", label: "Boys" },
        ]}
      />

      <Card
        id="real"
        title="The top 10, every spelling counted"
        lede={
          jump
            ? jump.i === 0
              ? `Counting its other spellings, ${entryFor(core, real[0][0]).name} is the most popular ${possessive} name, not #${fmt(real[0][2])}.`
              : `Counting its other spellings, ${entryFor(core, real[jump.i][0]).name} rises to #${jump.i + 1}, from #${fmt(real[jump.i][2])}.`
            : ahead
              ? `With its other spellings counted, ${entryFor(core, real[0][0]).name} was given to ${fmt(real[0][1])} ${word}, ${ahead < 2.5 ? "about twice" : `${Math.round(ahead)} times`} as many as ${entryFor(core, real[1][0]).name}.`
              : `Counting every spelling barely changes the order at the top for ${word}.`
        }
      >
        <ol class="rows trend-rows" aria-label={`Top 10 ${word}, ${ins.latest}, every spelling counted`}>
          {real.map(([k, count, own], i) => {
            const fam = familyOf(ins, k, si);
            const others = fam ? fam.members.filter((m) => m.key !== k) : [];
            return (
              <li key={k}>
                <button class="trend-row real" onClick={() => openName(k)}>
                  <span class="tr-rank">{i + 1}</span>
                  <span class="tr-namewrap">
                    <span class="tr-name">{entryFor(core, k).name}</span>
                    {others.length > 0 && (
                      <span class="tr-sub">
                        with {entryFor(core, others[0].key).name}
                        {others.length > 1 && (
                          <>
                            {" "}
                            <span aria-hidden="true">+{others.length - 1}</span>
                            <span class="sr-only">and {others.length - 1} more</span>
                          </>
                        )}
                      </span>
                    )}
                  </span>
                  <span class="tr-count">
                    {fmt(count)}
                    <span class="sr-only"> {word}</span>
                  </span>
                  <span class={`tr-alone ${own && own > i + 1 ? "up" : ""}`}>
                    {own ? `#${fmt(own)} alone` : "–"}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <p class="trend-note">
          Spellings count together when they sound the same, such as {c.id === "us" ? "Jackson and Jaxon" : "Sophia and Sofia"}. The
          official lists count every spelling as a different name.
        </p>
      </Card>

      {mv && up && down && (
        <Card
          id="moves"
          title={`Since ${ins.latest - 1}`}
          lede={`${entryFor(core, up[0]).name} climbed ${up[2] - up[1]} places to #${up[1]}, the biggest rise in the top 100. ${entryFor(core, down[0]).name} fell furthest, from #${down[2]} to #${down[1]}.`}
        >
          <div class="move-cols">
            <MoveList core={core} rows={mv.up.slice(0, 5)} dir="up" label="Climbing" />
            <MoveList core={core} rows={mv.down.slice(0, 5)} dir="down" label="Falling" />
          </div>
          {mv.in.length > 0 && (
            <p class="trend-note">
              New to the top 100: <NameList core={core} keys={mv.in.map((r) => r[0])} />.
            </p>
          )}
          {mv.out.length > 0 && (
            <p class="trend-note">
              Out of the top 100: <NameList core={core} keys={mv.out.map((r) => r[0])} />.
            </p>
          )}
        </Card>
      )}

      <Card
        id="variety"
        title="More names than ever"
        lede={`In ${ins.y0}, half of all ${word} had one of ${fmt(half0)} names. In ${ins.latest}, it took ${fmt(halfN)}.`}
      >
        <HistoryChart
          years={years}
          series={[{ id: "half", label: `Names covering half of all ${word}`, cls: si ? "s-boy" : "s-girl", values: v.half }]}
          mode="count"
          height={150}
          measure={`Names it took to cover half of all ${word}, each year`}
        />
        <p class="trend-note">
          The top 10 names went from {pct(v.top10[0])} of {word} in {ins.y0} to {pct(v.top10[v.top10.length - 1])} in {ins.latest}.
        </p>
      </Card>

      {ends.length > 0 && riser && (
        <Card
          id="endings"
          title="How names end"
          lede={`${si ? "Boys'" : "Girls'"} names ending in ${riser.e === "ee" ? "an ‘ee’ sound" : `-${riser.e}`} went from ${pct(riser.s[0])} of ${word} in ${ins.y0} to ${pct(riser.s[riser.s.length - 1])} in ${ins.latest}.`}
        >
          <ul class="end-grid">
            {ends.map((e) => {
              // named by what it shows (so voice control can say it), with words for screen readers
              const tile = (
                <>
                  <span class="end-label">{endingLabel(e.e)}</span>
                  <span class="end-figs">
                    <span class="sr-only">: </span>
                    {pct(e.s[0])}
                    <span class="sr-only"> of {word} in {ins.y0},</span> <span aria-hidden="true">→</span> {pct(e.s[e.s.length - 1])}
                    <span class="sr-only"> in {ins.latest}. </span>
                  </span>
                  <MiniLine values={e.s} max={endMax} cls={si ? "s-boy" : "s-girl"} />
                  <span class="end-ex">{e.ex.slice(0, 3).map((k) => entryFor(core, k).name).join(", ")}</span>
                </>
              );
              return (
                <li key={e.e}>
                  {e.e === "ee" ? (
                    <div class="end-tile">{tile}</div>
                  ) : (
                    <button class="end-tile" onClick={() => onEnding(e.e, si)}>
                      {tile}
                      <span class="sr-only">. See names ending in {e.e}</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <p class="trend-note">Share of all {word} each year. Tap an ending to see its names.</p>
        </Card>
      )}

      {cross.length > 0 && (
        <Card
          id="cross"
          title="Changing sides"
          lede={`In ${cross[0].firstYear}, ${share(cross[0].first)} of the babies named ${entryFor(core, cross[0].k).name} were girls. In ${cross[0].lastYear}, ${share(cross[0].last)} were.`}
        >
          <ol class="rows trend-rows">
            {cross.map((x) => (
              <li key={x.k}>
                <button class="trend-row cross" onClick={() => openName(x.k)}>
                  <span class="tr-name">{entryFor(core, x.k).name}</span>
                  <MiniLine values={x.s} max={1} mid={0.5} cls="s-girl" />
                  <span class="tr-sub">
                    {share(x.first)} girls in {x.firstYear}, {share(x.last)} {x.lastYear === ins.latest ? "now" : `in ${x.lastYear}`}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <p class="trend-note">The line is the share of babies given each name who were girls, with half marked.</p>
        </Card>
      )}

      {lead && (
        <Card
          id="atlantic"
          title="Across the Atlantic"
          lede={`${lead[0]} is #${fmt(lead[1])} for ${word} in ${lists[0].here}, but ${lead[2] ? `#${fmt(lead[2])}` : "not listed"} in ${lists[0].there}.`}
        >
          <div class="move-cols">
            {lists.map((l) => (
              <AtlanticList key={l.label} core={core} rows={l.rows} here={l.here} there={l.there} label={l.label} />
            ))}
          </div>
          <p class="trend-note">
            Compared by each country's share of {word}: the UK in {a.years.uk}, the US in {a.years.us}. Not listed means given to
            fewer than 3 babies in the UK, or 5 in the US.
          </p>
        </Card>
      )}
    </div>
  );
}
